import * as crypto from "crypto-js";
import { getConfig } from "./configService";
import {
  clearScreenCache,
  invalidateScreenCache,
  readCache,
  writeCache,
} from "./screenCache";
import { debugApi, logRequest as logNetworkRequest } from "./networkLogger";

// ============ КОНФИГУРАЦИЯ API ============
// Динамические URL из runtime конфига
// dev/prod: API_URL = полный путь без /api/cn
// gp-prod: API_URL = базовый URL + /api/cn
const getAuthApiBase = () => getConfig().AUTH_API_URL;
const getAuthApiUrl = () => `${getAuthApiBase()}`;
const getApiBaseUrl = () => getConfig().API_URL; // Используем API_URL напрямую

// Ключ шифрования для основного API
const ENCRYPTION_KEY = "secret-key";



// ============ КЭШИРОВАНИЕ ЗАПРОСОВ ============
// Кэш для предотвращения дублирующих запросов (429 Too Many Requests)
interface CacheEntry<T> {
  data: T;
  timestamp: number;
  promise?: Promise<T>;
}

const requestCache = new Map<string, CacheEntry<unknown>>();
const pendingRequests = new Map<string, Promise<unknown>>();

// Время жизни кэша (5 минут) — для оперативных данных основного бэкенда.
const CACHE_TTL = 5 * 60 * 1000;

/*
 * У витрин акима (`/akim/...`) свой TTL — час, и они же единственные, кто
 * переживает перезагрузку вкладки (`screenCache` на `localStorage`).
 *
 * ПОЧЕМУ ИМ МОЖНО ЧАС. Это суточные агрегаты ЦН: срез обновляется раз в сутки
 * (`extractDate` / `asOf` в ответе), а считается в БД по 40–90 секунд. Повторный
 * запрос через минуту вернёт те же цифры, заплатив за них минутой спиннера.
 * Остальным ручкам того же бэкенда такой TTL ставить нельзя — там данные меняются
 * в течение смены, поэтому у них прежние 5 минут и только память.
 *
 * ПРИЗНАК — ПУТЬ, А НЕ СЕРВИС. Раньше витрины отличались тем, что жили в
 * отдельном `ops-backend`; после переезда в общий бэкенд их видно только по
 * префиксу, и проверять надо именно его.
 */
const DASHBOARD_CACHE_TTL = 60 * 60 * 1000;

/*
 * `/vli/` — срезы ВЛИ, те же ручки, что у сайта: срез выгружается раз в месяц,
 * а экран «Труд» собирает динамику из десятка срезов — пятиминутный кэш
 * гонял бы их заново на каждом открытии.
 */
const isDashboardEndpoint = (endpoint: string): boolean =>
  endpoint.startsWith("/akim/") ||
  endpoint.startsWith("/vli/") ||
  // НОБД — суточный снимок; «Образование» собирает 12 месяцев зачислений.
  endpoint.startsWith("/nobd/") ||
  // РПН заливают дампом вручную, за час в нём не меняется ничего; экран
  // «Здравоохранение» — один `/rpn/summary`.
  endpoint.startsWith("/rpn/") ||
  // Витрины рисков — суточные прогоны; остальной `/risk/` (задачи, отчёты)
  // оперативный, ему часовой кэш нельзя.
  endpoint.startsWith("/risk/gbd-fl-person-statistics") ||
  endpoint.startsWith("/risk/underage-statistics") ||
  // «Общественная безопасность»: сводка ЕРАП считается ~9 с, два окна — два
  // прохода; час — тот же срок, что был у прежней /akim/public-safety.
  endpoint.startsWith("/erap/protocols/summary") ||
  endpoint.startsWith("/erdr/analytics/messages");

const cacheTtlFor = (endpoint: string): number =>
  isDashboardEndpoint(endpoint) ? DASHBOARD_CACHE_TTL : CACHE_TTL;

// Генерация ключа кэша
function getCacheKey(url: string, method: string, body?: unknown): string {
  const bodyHash = body ? JSON.stringify(body) : "";
  return `${method}:${url}:${bodyHash}`;
}

// Проверка актуальности кэша
function isCacheValid<T>(
  entry: CacheEntry<T> | undefined,
  ttl: number = CACHE_TTL,
): entry is CacheEntry<T> {
  if (!entry) return false;
  return Date.now() - entry.timestamp < ttl;
}

// Очистка устаревшего кэша (вызывается периодически)
function cleanupCache(): void {
  const now = Date.now();
  for (const [key, entry] of requestCache.entries()) {
    // По максимальному TTL: с `CACHE_TTL` уборщик сносил бы часовой кэш витрин
    // ЦН каждые пять минут, и персист в `localStorage` терял бы смысл.
    if (now - entry.timestamp > DASHBOARD_CACHE_TTL) {
      requestCache.delete(key);
    }
  }
}

// Очищаем кэш каждые 5 минут
setInterval(cleanupCache, CACHE_TTL);

// Принудительная очистка всего кэша (например, после logout)
export function clearApiCache(): void {
  requestCache.clear();
  pendingRequests.clear();
  // Иначе после выхода следующий пользователь увидит витрины предыдущего:
  // `localStorage` переживает и логаут, и перезагрузку вкладки.
  clearScreenCache();
}

// Инвалидация кэша для определенного URL паттерна
export function invalidateCache(urlPattern: string): void {
  for (const key of requestCache.keys()) {
    if (key.includes(urlPattern)) {
      requestCache.delete(key);
    }
  }
  invalidateScreenCache(urlPattern);
}

// Один сервис — `sc_atyrau_backend`. Второй (сервис поручений республиканского
// продукта) удалён 27.09.2026: у Атырау его нет.
export type ApiService = "main";

const getServiceUrls = (): Record<ApiService, string> => ({
  main: getApiBaseUrl(),
});

// Конфигурация сервисов
const SERVICE_CONFIG: Record<ApiService, { encrypted: boolean }> = {
  main: { encrypted: true },
};

// Типизированные ошибки API
export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export class UnauthorizedError extends ApiError {
  constructor(message = "Не авторизован") {
    super(message, 401, "UNAUTHORIZED");
    this.name = "UnauthorizedError";
  }
}

export class ForbiddenError extends ApiError {
  constructor(message = "Доступ запрещен") {
    super(message, 403, "FORBIDDEN");
    this.name = "ForbiddenError";
  }
}

export class NotFoundError extends ApiError {
  constructor(message = "Ресурс не найден") {
    super(message, 404, "NOT_FOUND");
    this.name = "NotFoundError";
  }
}

export class NetworkError extends Error {
  constructor(message = "Ошибка сети. Проверьте подключение к интернету") {
    super(message);
    this.name = "NetworkError";
  }
}

export class DecryptionError extends Error {
  constructor(message = "Ошибка расшифровки данных") {
    super(message);
    this.name = "DecryptionError";
  }
}

// ============ ТОКЕНЫ ============
// Проверка режима token auth (ограниченный доступ по ссылке)
export const isTokenAuthMode = (): boolean =>
  localStorage.getItem("isTokenAuth") === "true";

// Получение токена для API запросов
export const getAccessToken = (): string | null => {
  /*
   * ЗАПАСНЫХ ТОКЕНОВ НЕТ. Здесь возвращались два зашитых JWT республиканского
   * продукта (`SUPPORT_001`), если своего токена не было. `sc_atyrau_backend`
   * их не принимает (проверено: 401), так что они только выдавали чужой токен
   * за свой и лежали в бандле у всех. Нет токена — запрос уходит без него и
   * получает честный 401.
   */
  return localStorage.getItem("accessToken");
};

export const getRefreshToken = (): string | null =>
  localStorage.getItem("refreshToken");

export const setAccessToken = (token: string): void =>
  localStorage.setItem("accessToken", token);

export const setRefreshToken = (token: string): void =>
  localStorage.setItem("refreshToken", token);

export const removeAllTokens = (): void => {
  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken");
};

// Проверка авторизации
export function isAuthenticated(): boolean {
  return !!getAccessToken();
}

// ============ REFRESH TOKEN ============
/*
 * ОДИН REFRESH НА ВСЁ ПРИЛОЖЕНИЕ, И ЭТО ОБЯЗАТЕЛЬНО.
 *
 * Refresh-токен `sc_atyrau_backend` одноразовый и ротируется: каждый обмен
 * выдаёт новую пару, а повторное предъявление уже использованного токена
 * бэкенд считает кражей и закрывает ВСЕ сессии аккаунта
 * (`refresh_token_reused`). Раньше обновлений было два — здесь и в
 * `authService`, каждое со своей блокировкой, — и при загрузке страницы они
 * совпадали: `AuthContext` обновлял токен, а первые запросы с протухшим
 * токеном — ещё раз, тем же refresh-токеном. `authService.tryRefreshToken`
 * теперь просто ссылается сюда, а параллельные вызовы ждут одного обмена.
 *
 * ЗАПАСНОГО ВХОДА НЕТ. Раньше при отказе refresh планшет молча логинился
 * зашитой парой `superadmin` — то есть любой, у кого сессия кончилась,
 * оказывался под учёткой администратора. Теперь отказ — это выход.
 */
type RefreshOutcome = "refreshed" | "rejected" | "unavailable";

let refreshPromise: Promise<RefreshOutcome> | null = null;

/** Событие для `AuthContext`: сессию больше не продлить, нужен вход. */
export const SESSION_EXPIRED_EVENT = "auth:session-expired";
const SESSION_EXPIRED_NOTICE_KEY = "auth:session-expired-notice";

/*
 * ВЫХОД ТОЛЬКО КОГДА БЭКЕНД СКАЗАЛ «НЕТ», А НЕ КОГДА ОН МОЛЧИТ. Стенд падает
 * (22.09.2026 — пять раз за день), и выкидывать людей на форму входа из-за
 * сетевой ошибки или 502 значит терять живую сессию: сервер поднимется, и
 * тот же refresh-токен сработает. Поэтому исходов три, а выход — только при
 * `rejected`.
 */
async function performRefresh(): Promise<RefreshOutcome> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return "rejected";

  let response: Response;
  try {
    response = await fetch(`${getAuthApiUrl()}/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refreshToken }),
    });
  } catch {
    return "unavailable";
  }

  // 401/403 — токен отозван, истёк (30 дней) или уже предъявлялся.
  if (response.status === 401 || response.status === 403) return "rejected";
  if (!response.ok) return "unavailable";

  try {
    const data = await response.json();
    if (!data?.accessToken) return "unavailable";
    setAccessToken(data.accessToken);
    if (data.refreshToken) setRefreshToken(data.refreshToken);
    return "refreshed";
  } catch {
    return "unavailable";
  }
}

/*
 * СРАБАТЫВАЕТ ОДИН РАЗ НА СЕССИЮ. Пока идёт выход, запросы экрана, уже
 * улетевшие без токена, возвращают 401 и снова зовут сюда. Без проверки они
 * повторно ставили отметку «сессия истекла» уже после того, как форма входа
 * её забрала, — и человек, позже вышедший сам, видел ложное «сессия истекла».
 * Нет токенов — сессии уже нет, сообщать нечего.
 */
function expireSession(): void {
  const hadSession = !!getAccessToken() || !!getRefreshToken();
  removeAllTokens();
  if (!hadSession) return;
  try {
    sessionStorage.setItem(SESSION_EXPIRED_NOTICE_KEY, "1");
  } catch {
    /* приватный режим: форма входа просто не скажет «сессия истекла» */
  }
  window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
}

/*
 * Отметка для формы входа: объяснить человеку, куда делась сессия. Чтение и
 * удаление разведены намеренно — StrictMode в dev вызывает инициализатор
 * `useState` дважды, и «прочитать и удалить» там оставило бы форму без
 * сообщения в зависимости от того, какой из двух результатов взял React.
 */
export function hasSessionExpiredNotice(): boolean {
  try {
    return sessionStorage.getItem(SESSION_EXPIRED_NOTICE_KEY) === "1";
  } catch {
    return false;
  }
}

export function clearSessionExpiredNotice(): void {
  try {
    sessionStorage.removeItem(SESSION_EXPIRED_NOTICE_KEY);
  } catch {
    /* нечего чистить */
  }
}

/** Обменять refresh-токен на новую пару. `false` — продлить не удалось. */
export function tryRefreshToken(): Promise<boolean> {
  if (!refreshPromise) {
    refreshPromise = performRefresh()
      .then((outcome) => {
        if (outcome === "rejected") expireSession();
        return outcome;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise.then((outcome) => outcome === "refreshed");
}

/*
 * ОБНОВЛЕНИЕ ЗАРАНЕЕ, ПО `exp` ТОКЕНА, А НЕ ПО ФАКТУ 401.
 *
 * Access-токен живёт 15 минут. Без этого каждый запрос, ушедший после
 * истечения, получал 401, и только потом шёл refresh и повтор — на загрузке
 * экрана это пачка красных 401 и удвоенные запросы (журнал 27.09.2026). За 30
 * секунд до конца срока токен обновляется один раз, до отправки запроса.
 */
const REFRESH_AHEAD_SECONDS = 30;

function secondsUntilExpiry(token: string): number | null {
  try {
    const payload = JSON.parse(
      atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")),
    );
    return typeof payload.exp === "number" ? payload.exp - Date.now() / 1000 : null;
  } catch {
    return null;
  }
}

export async function ensureFreshAccessToken(): Promise<void> {
  const token = getAccessToken();
  if (!token || !getRefreshToken()) return;
  const left = secondsUntilExpiry(token);
  if (left === null || left > REFRESH_AHEAD_SECONDS) return;
  await tryRefreshToken();
}

// Расшифровка данных AES (как в оригинале)
export function decryptData<T>(encryptedData: string): T | null {
  try {
    const bytes = crypto.AES.decrypt(encryptedData, ENCRYPTION_KEY);
    const text = bytes.toString(crypto.enc.Utf8);

    return text ? (JSON.parse(text) as T) : null;
  } catch {
    return null;
  }
}

// Проверка, является ли строка зашифрованными данными
function isEncryptedData(data: unknown): data is string {
  if (typeof data !== "string") return false;
  // Зашифрованные данные начинаются с "U2FsdGVkX1" (Base64 "Salted__")
  return data.trim().startsWith("U2FsdGVkX1");
}

// Обработка ответа с ошибкой
async function handleErrorResponse(response: Response): Promise<never> {
  let errorMessage = "Неизвестная ошибка";
  let errorCode: string | undefined;

  try {
    const text = await response.text();

    // Пробуем расшифровать если зашифровано
    if (isEncryptedData(text)) {
      const decrypted = decryptData<{
        message?: string;
        error?: string;
        code?: string;
        details?: {
          response?: {
            error?: string;
            code?: string;
          };
        };
      }>(text);
      if (decrypted) {
        errorMessage =
          decrypted.message ||
          decrypted.error ||
          decrypted.details?.response?.error ||
          errorMessage;
        errorCode = decrypted.code || decrypted.details?.response?.code;
      }
    } else {
      // Обычный JSON
      const errorData: {
        message?: string;
        error?: string;
        code?: string;
        details?: {
          response?: {
            error?: string;
            code?: string;
          };
        };
      } = JSON.parse(text);

      errorMessage =
        errorData.message ||
        errorData.error ||
        errorData.details?.response?.error ||
        errorMessage;
      errorCode = errorData.code || errorData.details?.response?.code;
    }
  } catch {
    // Не удалось распарсить ответ
  }

  switch (response.status) {
    case 401:
      // Не удаляем токены автоматически, пусть пользователь сам выходит
      throw new UnauthorizedError(errorMessage);
    case 403:
      throw new ForbiddenError(errorMessage);
    case 404:
      throw new NotFoundError(errorMessage);
    default:
      throw new ApiError(errorMessage, response.status, errorCode);
  }
}

// Опции для запроса
interface RequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  skipAuth?: boolean;
  service?: ApiService;
  params?: Record<string, string | number | boolean | undefined | null>;
  skipCache?: boolean; // Пропустить кэш (для принудительного обновления данных)
}

// Базовый API клиент
export async function apiRequest<T>(
  endpoint: string,
  options: RequestOptions = {},
): Promise<T> {
  const {
    body,
    skipAuth = false,
    service = "main",
    params,
    skipCache = false,
    ...fetchOptions
  } = options;

  const baseUrl = getServiceUrls()[service];
  const queryString = params ? buildQueryString(params) : "";
  const fullEndpoint = `${endpoint}${queryString}`;
  const fullUrl = `${baseUrl}${fullEndpoint}`;
  const method = (fetchOptions.method || "GET").toUpperCase();

  // Кэшируем только GET запросы
  const shouldCache = method === "GET" && !skipCache;

  const cacheTtl = cacheTtlFor(fullEndpoint);
  /*
   * СЫРЫЕ ОТВЕТЫ В `localStorage` НЕ ПИШУТСЯ (с 29.09.2026) — только в память на
   * час. Экраны сами кладут туда собранную сводку (`cachedCall` в сервисах): она
   * весит килобайты, а сырьё — мегабайты (38 страниц рождений РПН по 200 строк)
   * и вытесняло квоту ~5 МБ; `writeCache` при переполнении чистит весь кэш, и
   * после перезагрузки вкладки экран качал всё заново. Персист остался только
   * у `/akim/` — ими пользуется лишь неоткрываемый `AkimSectorModule`.
   */
  const shouldPersist = shouldCache && fullEndpoint.startsWith("/akim/");

  if (shouldCache) {
    const cacheKey = getCacheKey(fullUrl, method, body);

    // Проверяем кэш
    const cachedEntry = requestCache.get(cacheKey) as CacheEntry<T> | undefined;
    if (isCacheValid(cachedEntry, cacheTtl)) {
      debugApi("memory", method, fullUrl);
      return cachedEntry.data;
    }

    // Память пуста (перезагрузили вкладку) — пробуем `localStorage`.
    if (shouldPersist) {
      const persisted = readCache<T>(cacheKey);
      if (persisted !== null) {
        requestCache.set(cacheKey, { data: persisted, timestamp: Date.now() });
        debugApi("storage", method, fullUrl);
        return persisted;
      }
    }

    // Проверяем pending запросы (дедупликация)
    const pendingRequest = pendingRequests.get(cacheKey) as
      | Promise<T>
      | undefined;
    if (pendingRequest) {
      debugApi("shared", method, fullUrl);
      return pendingRequest;
    }
  }

  const executeRequest = async (): Promise<T> => {
    const _reqStartTime = Date.now();
    let _reqStatus: number | null = null;
    let _reqStatusText: string | undefined;

    /*
     * Адрес для журнала — тот, куда запрос уходит НА САМОМ ДЕЛЕ.
     *
     * `fullUrl` относительный (`/api/v1/...`), потому что запросы идут через
     * прокси Vite: так они остаются same-origin и не требуют CORS. Но в журнале
     * из-за этого не было видно, на какой стенд смотрит планшет, — а это первый
     * вопрос, когда данные не те. Подставляем цель прокси из
     * `VITE_SC_PROXY_TARGET`, то есть показываем `http://192.168.2.246:8181/...`
     * при неизменном поведении запроса.
     *
     * ТОЛЬКО ДЛЯ `/api/` — других бэкендов у планшета нет. Адрес вне `/api/`
     * пишется в журнал как есть: додумывать цель значит записать неправду.
     */
    const _logUrl = (() => {
      const target = import.meta.env.VITE_SC_PROXY_TARGET;
      if (!target || !fullUrl.startsWith("/api/")) return fullUrl;
      return `${String(target).replace(/\/$/, "")}${fullUrl}`;
    })();
    /*
     * Тело ответа с ошибкой — для журнала запросов (`/network-logs`).
     *
     * Читается отдельно, потому что `handleErrorResponse` поглощает поток
     * ответа и сразу бросает исключение: после него у `catch` на руках только
     * текст сообщения, а объяснение сервера (`code`, `message`, `requestId`)
     * уже недоступно. Без этого журнал показывал ошибки без единой подробности
     * — ровно в тех случаях, ради которых в него и заходят.
     */
    let _errorBody: unknown;

    const _doLog = (responseBody: unknown, errorMsg?: string) => {
      debugApi("network", method, fullUrl, Date.now() - _reqStartTime, _reqStatus);
      logNetworkRequest({
        id: Math.random().toString(36).slice(2),
        timestamp: _reqStartTime,
        method,
        url: _logUrl,
        service,
        requestBody: body,
        status: _reqStatus,
        statusText: _reqStatusText,
        responseBody,
        duration: Date.now() - _reqStartTime,
        error: errorMsg,
      });
    };

    try {
      if (!skipAuth) await ensureFreshAccessToken();
      const token = getAccessToken();
      const config = SERVICE_CONFIG[service];

      const headers: HeadersInit = {
        "Content-Type": "application/json",
        ...(!skipAuth && token && { Authorization: `Bearer ${token}` }),
        ...fetchOptions.headers,
      };

      const makeRequest = async (hdrs: HeadersInit) => {
        return fetch(fullUrl, {
          ...fetchOptions,
          headers: hdrs,
          body: body ? JSON.stringify(body) : undefined,
        });
      };

      let response = await makeRequest(headers);

      // При 429 (Too Many Requests) - ждём и повторяем с экспоненциальной задержкой
      const MAX_RETRIES = 5;
      let retryCount = 0;
      while (response.status === 429 && retryCount < MAX_RETRIES) {
        retryCount++;
        const retryAfter = response.headers.get("Retry-After");
        // Базовая задержка: из заголовка или экспоненциальная (1s, 2s, 4s, 8s, 16s)
        const baseDelay = retryAfter
          ? parseInt(retryAfter) * 1000
          : Math.pow(2, retryCount - 1) * 1000;
        // Добавляем случайный jitter (0-500ms) чтобы запросы не повторялись одновременно
        const jitter = Math.random() * 500;
        const delay = baseDelay + jitter;
        await new Promise((resolve) => setTimeout(resolve, delay));
        response = await makeRequest(headers);
      }

      // При 401 пробуем обновить токен и повторить запрос
      if (response.status === 401 && !skipAuth) {
        const refreshed = await tryRefreshToken();

        if (refreshed) {
          // Повторяем запрос с новым токеном
          const newToken = getAccessToken();
          const newHeaders: HeadersInit = {
            "Content-Type": "application/json",
            ...(newToken && { Authorization: `Bearer ${newToken}` }),
            ...fetchOptions.headers,
          };

          response = await fetch(fullUrl, {
            ...fetchOptions,
            headers: newHeaders,
            body: body ? JSON.stringify(body) : undefined,
          });

          // Свежий токен и всё равно 401 — продлевать нечего, нужен вход.
          if (response.status === 401) expireSession();
        }
      }

      _reqStatus = response.status;
      _reqStatusText = response.statusText;

      if (!response.ok) {
        /*
         * Клон, а не сам ответ: оригинал нужен `handleErrorResponse` целым.
         *
         * ЗАПАСНОЙ ВАРИАНТ ТЕКСТОМ — НЕ ПЕДАНТИЗМ. Отказ приходит JSON'ом только
         * когда до приложения дошло. Упавший стенд отвечает HTML-страницей
         * nginx, `json()` на ней ломается, и в журнале оставалась пустота при
         * бесполезном «Неизвестная ошибка» — то есть ровно в том случае, ради
         * которого в журнал и заходят. Теперь там видно «502 Bad Gateway nginx».
         */
        try {
          _errorBody = await response.clone().json();
        } catch {
          try {
            const text = (await response.clone().text()).trim();
            // Разметку сворачиваем: на экране нужен смысл, а не заглушка nginx
            // из шести строк HTML-комментариев.
            const plain = text
              .replace(/<[^>]*>/g, " ")
              .replace(/\s+/g, " ")
              .trim();
            _errorBody = plain ? { raw: plain.slice(0, 500) } : undefined;
          } catch {
            _errorBody = undefined;
          }
        }
        await handleErrorResponse(response);
      }

      // Для 204 No Content возвращаем пустой объект
      if (response.status === 204) {
        _doLog({});
        return {} as T;
      }

      // Получаем текст ответа
      let responseText = await response.text();
      // Удаляем кавычки в начале и в конце строки, если они есть
      if (
        responseText.length >= 2 &&
        responseText.startsWith('"') &&
        responseText.endsWith('"')
      ) {
        responseText = responseText.slice(1, -1);
      }

      // Если пустой ответ
      if (!responseText.trim()) {
        _doLog({});
        return {} as T;
      }
      // Проверяем нужно ли расшифровывать
      if (config.encrypted && isEncryptedData(responseText)) {
        const decrypted = decryptData<T>(responseText);
        if (decrypted === null) {
          throw new DecryptionError("Не удалось расшифровать ответ сервера");
        }
        _doLog(decrypted);
        return decrypted;
      }

      // Обычный JSON
      const _parsed = JSON.parse(responseText) as T;
      _doLog(_parsed);
      return _parsed;
    } catch (error) {
      _doLog(_errorBody, error instanceof Error ? error.message : String(error));
      if (error instanceof ApiError || error instanceof DecryptionError) {
        throw error;
      }

      if (error instanceof TypeError && error.message.includes("fetch")) {
        throw new NetworkError();
      }

      throw error;
    }
  };

  // Если кэширование включено - добавляем запрос в pending и сохраняем результат
  if (shouldCache) {
    const cacheKey = getCacheKey(fullUrl, method, body);

    const requestPromise = executeRequest()
      .then((result) => {
        // Сохраняем в кэш
        requestCache.set(cacheKey, {
          data: result,
          timestamp: Date.now(),
        });
        if (shouldPersist) {
          writeCache(cacheKey, result, cacheTtl);
        }
        // Удаляем из pending
        pendingRequests.delete(cacheKey);
        return result;
      })
      .catch((error) => {
        // Удаляем из pending при ошибке
        pendingRequests.delete(cacheKey);
        throw error;
      });

    // Добавляем в pending для дедупликации
    pendingRequests.set(cacheKey, requestPromise);

    return requestPromise;
  }

  // Без кэширования - просто выполняем запрос
  return executeRequest();
}

// Хелперы для разных HTTP методов (основной API)
export const api = {
  get: <T>(endpoint: string, options?: RequestOptions) =>
    apiRequest<T>(endpoint, { ...options, method: "GET" }),

  post: <T>(endpoint: string, body?: unknown, options?: RequestOptions) =>
    apiRequest<T>(endpoint, { ...options, method: "POST", body }),

  put: <T>(endpoint: string, body?: unknown, options?: RequestOptions) =>
    apiRequest<T>(endpoint, { ...options, method: "PUT", body }),

  patch: <T>(endpoint: string, body?: unknown, options?: RequestOptions) =>
    apiRequest<T>(endpoint, { ...options, method: "PATCH", body }),

  delete: <T>(endpoint: string, options?: RequestOptions) =>
    apiRequest<T>(endpoint, { ...options, method: "DELETE" }),
};

// ============ AUTH API ============
// Для совместимости с authService (axios-подобный интерфейс)
async function authRequest<T>(
  endpoint: string,
  options: {
    method: string;
    body?: unknown;
    headers?: Record<string, string>;
  } = { method: "GET" },
): Promise<{ data: T }> {
  const token = getAccessToken();
  const headers: HeadersInit = {
    "Content-Type": "application/json",
    ...(token && { Authorization: `Bearer ${token}` }),
    ...options.headers,
  };

  const response = await fetch(`${getAuthApiUrl()}${endpoint}`, {
    method: options.method,
    headers,
    body: options.body ? JSON.stringify(options.body) : undefined,
  });

  if (!response.ok) {
    const error = new Error("Request failed") as Error & {
      response?: { status: number };
    };
    error.response = { status: response.status };
    throw error;
  }

  const data = await response.json();
  return { data };
}

export const authApi = {
  get: <T>(endpoint: string) => authRequest<T>(endpoint, { method: "GET" }),

  post: <T>(
    endpoint: string,
    body?: unknown,
    config?: { headers?: Record<string, string> },
  ) =>
    authRequest<T>(endpoint, {
      method: "POST",
      body,
      headers: config?.headers,
    }),
};

// Хелпер для создания query string из объекта
export function buildQueryString(
  params: Record<string, string | number | boolean | undefined | null>,
): string {
  const queryParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      queryParams.append(key, String(value));
    }
  });

  const queryString = queryParams.toString();
  return queryString ? `?${queryString}` : "";
}
